import React, { useState } from 'react';
import classNames from 'classnames';
import { useSignupForm } from '../hooks/useSignupForm';
import { PersonalInfoStep } from './steps/PersonalInfoStep';
import { ContactInfoStep } from './steps/ContactInfoStep';
import { FCANumberStep } from './steps/FCANumberStep';
import { BusinessInfoStep } from './steps/BusinessInfoStep';
import { RoleCompanySizeStep } from './steps/RoleCompanySizeStep';
import { PlatformSetupStep } from './steps/PlatformSetupStep';
import { ReviewStep } from './steps/ReviewStep';
import { HoneypotFields } from './HoneypotFields';
import { SignupFormData } from '../types';
import { pushToDataLayer } from '../utils/analytics';

interface MultiStepFormProps {
  onSuccess?: (data: SignupFormData) => void;
  className?: string;
}

const STEPS = [
  { id: 1, title: 'Personal Details', description: 'Your name' },
  { id: 2, title: 'Contact Info', description: 'Email and phone' },
  { id: 3, title: 'FCA Number', description: 'Your FCA registration' },
  { id: 4, title: 'Company Details', description: 'Business information' },
  { id: 5, title: 'Role & Size', description: 'Your role and company size' },
  { id: 6, title: 'Platform Setup', description: 'Your Smartr365 address' },
  { id: 7, title: 'Agreement', description: 'Terms and finish' },
];

export const MultiStepForm: React.FC<MultiStepFormProps> = ({
  onSuccess,
  className,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  
  const {
    formData,
    errors,
    fcaValidation,
    businessNameValidation,
    networkSelection,
    isSubmitting,
    submitError,
    isSuccess,
    honeypotFields,
    updateHoneypotField,
    updateField,
    handleSubmit,
    canSubmit,
    onStepComplete,
  } = useSignupForm();

  const goToNextStep = () => {
    if (currentStep < STEPS.length) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      // Track step navigation in GTM
      pushToDataLayer({
        event: 'formStepCompleted',
        formStep: currentStep,
        formStepName: STEPS[currentStep - 1].title,
        nextStep: nextStep,
        nextStepName: STEPS[nextStep - 1].title,
      });
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      
      // Track backwards navigation in GTM
      pushToDataLayer({
        event: 'formStepBack',
        fromStep: currentStep,
        toStep: prevStep,
        toStepName: STEPS[prevStep - 1].title,
      });
    }
  };

  const onSubmit = async () => {
    await handleSubmit(onSuccess);
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-6">
          <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">
            Welcome to Smartr365!
          </h1>
          <p className="text-gray-600 mb-6">
            Your account has been created successfully. We'll be in touch shortly to complete your setup and get you started.
          </p>
          <div className="text-sm text-gray-500">
            Check your email for next steps
          </div>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInfoStep
            formData={formData}
            errors={errors}
            updateField={updateField}
            onNext={goToNextStep}
            onStepComplete={onStepComplete}
          />
        );
      case 2:
        return (
          <ContactInfoStep
            formData={formData}
            errors={errors}
            updateField={updateField}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onStepComplete={onStepComplete}
          />
        );
      case 3:
        return (
          <FCANumberStep
            formData={formData}
            errors={errors}
            fcaValidation={fcaValidation}
            updateField={updateField}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onStepComplete={onStepComplete}
          />
        );
      case 4:
        return (
          <BusinessInfoStep
            formData={formData}
            errors={errors}
            fcaValidation={fcaValidation}
            updateField={updateField}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onStepComplete={onStepComplete}
          />
        );
      case 5:
        return (
          <RoleCompanySizeStep
            formData={formData}
            errors={errors}
            updateField={updateField}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onStepComplete={onStepComplete}
          />
        );
      case 6:
        return (
          <PlatformSetupStep
            formData={formData}
            errors={errors}
            businessNameValidation={businessNameValidation}
            updateField={updateField}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onStepComplete={onStepComplete}
          />
        );
      case 7:
        return (
          <ReviewStep
            formData={formData}
            errors={errors}
            fcaValidation={fcaValidation}
            businessNameValidation={businessNameValidation}
            networkSelection={networkSelection}
            isSubmitting={isSubmitting}
            submitError={submitError}
            updateField={updateField}
            onSubmit={onSubmit}
            onPrevious={goToPreviousStep}
            canSubmit={canSubmit}
            onStepComplete={onStepComplete}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={classNames('min-h-screen bg-white flex', className)} style={{ fontFamily: 'Outfit' }}>
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <div className="max-w-md mx-auto px-8 py-12 flex-1">
          {/* Smartr365 Logo */}
          <div className="mb-10">
            <img 
              src="/smartr365-logo.svg" 
              alt="Smartr365" 
              className="h-6 w-auto"
            />
          </div>

          {/* Step Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-500">
                {currentStep} of {STEPS.length}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Form Content */}
          <div className="space-y-6">
            {renderStep()}
          </div>

          {/* Honeypot Fields (hidden from users, visible to bots) */}
          <HoneypotFields
            values={honeypotFields}
            onChange={updateHoneypotField}
          />
        </div>
      </div>

      {/* Right Side - Marketing Content (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 lg:h-screen lg:sticky lg:top-0" style={{
        backgroundImage: 'url(/bg-pattern.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '5vh 4vw',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div className="flex flex-col items-center text-center" style={{ 
          width: '100%',
          maxWidth: '90%',
          gap: 'clamp(24px, 4vh, 48px)'
        }}>
          {/* Main Headline */}
          <h1 style={{ 
            color: '#213547',
            textAlign: 'center',
            fontFamily: 'Outfit',
            fontSize: 'clamp(32px, 4vw, 48px)',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '1.2',
            margin: 0,
            maxWidth: '90%'
          }}>
            Start your FREE<br />3-month trial and save £285 per user
          </h1>
          
          {/* Subheadline */}
          <p style={{
            color: '#213547',
            textAlign: 'center',
            fontFamily: 'Outfit',
            fontSize: 'clamp(14px, 1.5vw, 18px)',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '1.6',
            margin: 0,
            maxWidth: '85%'
          }}>
            Join the most extensive mortgage & protection platform in the UK
          </p>
          
          {/* Product Shot Image */}
          <div className="w-full flex justify-center items-center" style={{ maxHeight: '60vh' }}>
            <img 
              src="/product-shot.png" 
              alt="Smartr365 Platform Interface" 
              className="rounded-lg shadow-2xl"
              style={{ 
                width: '100%',
                maxWidth: '90%',
                height: 'auto',
                maxHeight: '50vh',
                objectFit: 'contain'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
